import { useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Panel } from "@/components/PageHeader";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Sailboat, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const download = async (formato, anno) => {
  try {
    const { data } = await api.get("/admin/equipaggi/export", { params: { formato }, responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data);
    a.download = `elenco_equipaggi_${anno}.${formato}`;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch {
    toast.error("Esportazione non riuscita");
  }
};

const Missing = () => <span className="text-amber-700 text-xs">Ruolo non indicato</span>;

export const Equipaggi = () => {
  const [rows, setRows] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const anno = new Date().getFullYear();
  const load = () => api.get("/admin/equipaggi").then((r) => setRows(r.data));
  useEffect(() => {
    load();
  }, []);
  const importRanking = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/barche/import-ranking");
      setRes(data);
      toast.success(`${data.create} barche create dai ranking`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  if (!rows) return null;
  const needle = q.trim().toLowerCase();
  const shown = needle ? rows.filter((r) => [r.categoria, r.numero_velico, r.timoniere, r.prodiere, r.equipaggio].some((v) => (v || "").toLowerCase().includes(needle))) : rows;
  return (
    <Panel>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h2 className="font-heading text-2xl uppercase">Elenco equipaggi {anno}</h2>
        {isAdmin && <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={busy} onClick={importRanking} title="Crea le barche dei timonieri dai ranking Nacra 15 e 29er 2026" data-testid="equipaggi-import-ranking-button"><Sailboat className="h-4 w-4 mr-1" />{busy ? "Importazione..." : "Barche da ranking 2026"}</Button>
          <Button variant="outline" size="sm" onClick={() => download("xlsx", anno)} data-testid="equipaggi-export-xlsx-button"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
          <Button variant="outline" size="sm" onClick={() => download("pdf", anno)} data-testid="equipaggi-export-pdf-button"><FileText className="h-4 w-4 mr-1" />PDF</Button>
        </div>}
      </div>
      {res && (
        <div className="text-sm border rounded-md p-3 mb-4 bg-muted/30" data-testid="equipaggi-import-result">
          <p>Barche create {res.create} · già presenti {res.gia_presenti} · timonieri non soci {res.timonieri_non_trovati.length}</p>
          {res.timonieri_non_trovati.length > 0 && <p className="text-xs text-amber-700 mt-1">Non trovati: {res.timonieri_non_trovati.join(" · ")}</p>}
          {res.abbinati_simili.length > 0 && <p className="text-xs text-muted-foreground mt-1">Abbinati per nome simile: {res.abbinati_simili.join(" · ")}</p>}
        </div>
      )}
      <p className="text-sm text-muted-foreground mb-4">Barche indicate nelle iscrizioni dell'anno in corso · {rows.length} equipaggi{shown.length !== rows.length && ` · ${shown.length} trovati`}</p>
      <div className="relative w-full sm:w-80 mb-4">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder="Cerca classe, numero velico, timoniere o prodiere" value={q} onChange={(e) => setQ(e.target.value)} data-testid="equipaggi-search-input" />
      </div>
      {rows.length === 0 ? (
        <p className="text-muted-foreground" data-testid="equipaggi-empty">Nessun equipaggio iscritto per il {anno}.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid="equipaggi-table">
            <TableHeader>
              <TableRow><TableHead>Classe</TableHead><TableHead>Numero velico</TableHead><TableHead>Timoniere</TableHead><TableHead>Prodiere</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {shown.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">Nessun equipaggio trovato</TableCell></TableRow>}
              {shown.map((r) => (
                <TableRow key={r.id} data-testid={`equipaggio-row-${r.id}`}>
                  <TableCell className="font-medium">{r.categoria}</TableCell>
                  <TableCell className="font-mono-x">{r.numero_velico}</TableCell>
                  {r.timoniere ? (
                    <><TableCell>{r.timoniere}</TableCell><TableCell>{r.prodiere}</TableCell></>
                  ) : (
                    <TableCell colSpan={2}>{r.equipaggio} · <Missing /></TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Panel>
  );
};
